import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileText, Banknote, Clock, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { formatTL, monthLabel, formatDate } from '@/lib/format';
import { StatusBadge, Empty } from '@/components/shared';

export default function AdminPayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () => api.get('/admin/payouts').then((r) => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const runPayouts = async () => {
    if (!window.confirm('Onaylanmış tüm hak edişler bu dönem ödemesi olarak işlenecek. Devam edilsin mi?')) return;
    setRunning(true);
    try {
      const r = await api.post('/admin/payouts/run');
      toast.success(`${r.data.paid} mimara dönem ödemesi yapıldı`);
      load();
    } catch { toast.error('Ödeme işlemi başarısız'); }
    finally { setRunning(false); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch { toast.error('İndirme başarısız'); }
  };

  if (loading) return <div className="text-navy/40 text-sm">Yükleniyor...</div>;

  const ready = data?.ready || [];
  const history = data?.history || [];

  return (
    <div className="fade-up" data-testid="admin-payouts">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h1 className="font-serif text-3xl text-navy">Hakediş Yönetimi</h1>
        <div className="flex gap-2">
          <button onClick={runPayouts} disabled={running || ready.length === 0} data-testid="run-payouts"
            className="inline-flex items-center gap-1.5 text-sm bg-greenx text-cream px-3.5 py-2 rounded-md active:scale-95 transition-transform disabled:opacity-50">
            <Banknote strokeWidth={1.5} className="w-4 h-4" /> {running ? 'İşleniyor...' : 'Dönem ödemesini çalıştır'}
          </button>
          <button onClick={() => downloadBlob('/admin/payouts/bank-export?status=ready', 'banka-transfer.csv')} data-testid="bank-export"
            className="inline-flex items-center gap-1.5 text-sm border border-navy/20 text-navy px-3.5 py-2 rounded-md active:scale-95 transition-transform">
            <Download strokeWidth={1.5} className="w-4 h-4" /> Banka CSV
          </button>
        </div>
      </div>
      <p className="text-navy/50 text-sm mb-6">
        Komisyonlar iade süresi dolunca onaylanır. Onaylanan hak edişler her ay düzenli ödenir
        {data?.payoutDayOfMonth ? ` (ödeme günü: her ayın ${data.payoutDayOfMonth}'i)` : ''}.
      </p>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-cream border border-navy/10 rounded-xl p-4" data-testid="summary-ready">
          <CheckCircle2 strokeWidth={1.25} className="w-5 h-5 text-greenx mb-2" />
          <p className="font-serif text-2xl text-navy">{formatTL(data?.readyTotal || 0)}</p>
          <p className="text-xs text-navy/50 mt-1">Ödemeye hazır (Onaylandı)</p>
        </div>
        <div className="bg-cream border border-navy/10 rounded-xl p-4" data-testid="summary-pending">
          <Clock strokeWidth={1.25} className="w-5 h-5 text-gold mb-2" />
          <p className="font-serif text-2xl text-navy">{formatTL(data?.pendingTotal || 0)}</p>
          <p className="text-xs text-navy/50 mt-1">Onay bekleyen</p>
        </div>
      </div>

      {/* Ready to pay */}
      <h2 className="font-serif text-lg text-navy mb-3">Ödemeye Hazır</h2>
      {ready.length === 0 ? (
        <Empty text="Ödemeye hazır hak ediş yok" />
      ) : (
        <div className="overflow-x-auto mb-10">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-navy/50 text-xs border-b border-navy/10">
                <th className="py-3 pr-4 font-medium">Mimar</th>
                <th className="py-3 pr-4 font-medium">IBAN</th>
                <th className="py-3 pr-4 font-medium">Dağılım</th>
                <th className="py-3 pr-4 font-medium">Tutar</th>
              </tr>
            </thead>
            <tbody data-testid="ready-table">
              {ready.map((r) => (
                <tr key={r.architectId} className="border-b border-navy/10">
                  <td className="py-3 pr-4 text-navy font-medium">
                    {r.architectName}
                    {r.vip && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-gold/15 text-gold">VIP</span>}
                  </td>
                  <td className="py-3 pr-4 text-navy/50 text-xs">{r.iban || '-'}</td>
                  <td className="py-3 pr-4 text-navy/60 text-xs">
                    Kom. {formatTL(r.commissionAmount)}
                    {r.referralAmount > 0 && <span className="text-gold"> · Davet {formatTL(r.referralAmount)}</span>}
                    {r.clubCardAmount > 0 && <span className="text-gold"> · Kulüp {formatTL(r.clubCardAmount)}</span>}
                    {r.adjustmentAmount !== 0 && <span className="text-redx"> · Düzeltme {formatTL(r.adjustmentAmount)}</span>}
                  </td>
                  <td className="py-3 pr-4 font-medium text-greenx">{formatTL(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paid history */}
      <h2 className="font-serif text-lg text-navy mb-3">Ödeme Geçmişi</h2>
      {history.length === 0 ? (
        <Empty text="Ödeme kaydı yok" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-navy/50 text-xs border-b border-navy/10">
                <th className="py-3 pr-4 font-medium">Mimar</th>
                <th className="py-3 pr-4 font-medium">Dönem</th>
                <th className="py-3 pr-4 font-medium">Tutar</th>
                <th className="py-3 pr-4 font-medium">Durum</th>
                <th className="py-3 pr-4 font-medium">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid="payouts-table">
              {history.map((p) => (
                <tr key={p.id} className="border-b border-navy/10">
                  <td className="py-3 pr-4 text-navy font-medium">{p.architectName}</td>
                  <td className="py-3 pr-4 text-navy/70">{monthLabel(p.period)}{p.paidAt ? ` · ${formatDate(p.paidAt)}` : ''}</td>
                  <td className="py-3 pr-4 font-medium text-navy">{formatTL(p.amount)}</td>
                  <td className="py-3 pr-4"><StatusBadge status={p.status} /></td>
                  <td className="py-3 pr-4">
                    <button onClick={() => downloadBlob(`/admin/payouts/${p.id}/receipt`, `dekont-${p.period}.pdf`)} data-testid={`receipt-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs border border-navy/20 text-navy/70 px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                      <FileText className="w-3 h-3" /> Dekont
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
